import { useMediaQuery, useTheme } from '@mui/material';

export interface DeviceCapabilities {
  /**
   * Touch-first device — phone or tablet, **including large iPads**. This is
   * the capability axis: use it to decide camera capture, tap-vs-hover
   * affordances, and minimum touch-target sizing. Independent of viewport
   * width, so a 1366px iPad Pro still reports `isTouch: true`.
   */
  isTouch: boolean;
  /**
   * Narrow viewport. This is the **layout** axis — use it for drawer-vs-popover,
   * column counts, and spacing. Do NOT use it to infer device capability
   * (a wide tablet is compact-viewport=false but touch=true).
   */
  isCompactViewport: boolean;
  /**
   * Whether to offer in-app camera capture (`<input capture>`). True on touch
   * devices, which have a camera; desktops get the normal file picker. Note
   * the `capture` attribute is silently ignored on desktop, but gating the
   * camera *button* on this keeps the desktop UI clean.
   */
  canCapturePhoto: boolean;
}

/**
 * Single source of truth for device capability vs viewport-size decisions.
 *
 * Why this exists: scattered `useMediaQuery(theme.breakpoints.down('sm'|'md'))`
 * checks conflated "narrow screen" with "touch device / has camera". Large
 * tablets (iPad Pro) have wide viewports but ARE touch devices with cameras,
 * so width-based gating broke photo upload, hover-only tooltips, and
 * touch-target sizing on them.
 *
 * Detection strategy (capability axis):
 *  - `(hover: none) and (pointer: coarse)` — the primary, accurate signal for
 *    a touch-primary device (true on phones and tablets regardless of width).
 *  - `navigator.maxTouchPoints` on a Mac-like platform — iPadOS 13+ Safari can
 *    masquerade as desktop Safari (`platform === 'MacIntel'`), but a real Mac
 *    reports `maxTouchPoints === 0` while an iPad reports `> 1`. This catches
 *    the masquerade case the media query might miss.
 */
export function useDeviceCapabilities(): DeviceCapabilities {
  const theme = useTheme();

  // Layout axis — viewport width only.
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('md'));

  // Capability axis — input modality, independent of width.
  const coarseNoHover = useMediaQuery('(hover: none) and (pointer: coarse)');

  // iPad-in-desktop-mode disambiguation. Guarded for SSR / jsdom.
  const iPadMasquerade =
    typeof navigator !== 'undefined' &&
    navigator.maxTouchPoints > 1 &&
    /Mac/.test(navigator.platform ?? '');

  const isTouch = coarseNoHover || iPadMasquerade;

  return {
    isTouch,
    isCompactViewport,
    canCapturePhoto: isTouch,
  };
}
